import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { EventPanelFormType } from '@mark43/cad-api';
import { Divider, Box, DataList, DataItem } from '@cad-arc';
import { UseFormReturn } from 'react-hook-form';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { globalAttributes } from '../common/global-attributes';
import { displayMultiSelectFieldsValue } from '../common/helpers/display-multiselect-field-value';
import { getEventSubdivisionFieldsParams } from '../common/helpers/get-event-subdivision-fields-params';
import { EventLabelsView } from '../event-labels-view';
import { EventFormFieldsByAgencyGlobalType } from './fr-event-form-fields-by-agency-global-type';

export function FrEventFormFieldsSummary({
    eventForm,
    agencyTypeGlobalAttrId,
}: {
    eventForm: UseFormReturn<EventPanelFormType>;
    agencyTypeGlobalAttrId: number;
}) {
    const {
        multiAgencySubdivisions,
        additionalCallForService,
        eventsHaveRiskLevel,
    } = useEnabledFeatures();
    const bootstrapState = useBootstrapState();

    const allSubdivisionOptions =
        bootstrapState.subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView;
    const agencySubdivisionOptions =
        bootstrapState.subdivisionOptionsBootstrapResponse
            ?.agencyIdToSubdivisionOptionsBootstrapView;

    const isPoliceAgency = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;
    const isFireAgency = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.FIRE;
    const isEmsAgency = agencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.EMS;

    const {
        locationNotes,
        policeDispatchArea,
        policeAgency,
        policePrimaryRadioChannel,
        fireDispatchArea,
        fireAgency,
        firePrimaryRadioChannel,
        emsDispatchArea,
        emsAgency,
        emsPrimaryRadioChannel,
        callForService,
        additionalCallForServices,
        riskLevel,
        priority,
        alarmLevel,
        eventOrigin,
        eventQueue,
        eventLabels,
    } = eventForm.watch();

    const policeSubdivisionsFieldsValues = getEventSubdivisionFieldsParams({
        eventClassPrefix: 'police',
        agencyValue: policeAgency,
        multiAgencySubdivisions,
        allSubdivisionOptions,
        agencySubdivisionOptions,
    });
    const fireSubdivisionsFieldsValues = getEventSubdivisionFieldsParams({
        eventClassPrefix: 'fire',
        agencyValue: fireAgency,
        multiAgencySubdivisions,
        allSubdivisionOptions,
        agencySubdivisionOptions,
    });
    const emsSubdivisionsFieldsValues = getEventSubdivisionFieldsParams({
        eventClassPrefix: 'ems',
        agencyValue: emsAgency,
        multiAgencySubdivisions,
        allSubdivisionOptions,
        agencySubdivisionOptions,
    });

    return (
        <Box width="100%">
            <DataList>
                <DataItem dataKey="Location Notes">{locationNotes}</DataItem>
            </DataList>
            <Divider style={{ marginTop: '16px', marginBottom: '16px' }} />
            {isPoliceAgency && (
                <EventFormFieldsByAgencyGlobalType
                    isPoliceAgency
                    eventForm={eventForm}
                    subdivisionsFieldsValues={policeSubdivisionsFieldsValues}
                    dispatchAreaValue={policeDispatchArea}
                    agencyValue={policeAgency}
                    primaryRadioChannelValue={policePrimaryRadioChannel}
                />
            )}
            {isFireAgency && (
                <EventFormFieldsByAgencyGlobalType
                    eventForm={eventForm}
                    subdivisionsFieldsValues={fireSubdivisionsFieldsValues}
                    dispatchAreaValue={fireDispatchArea}
                    agencyValue={fireAgency}
                    primaryRadioChannelValue={firePrimaryRadioChannel}
                />
            )}
            {isEmsAgency && (
                <EventFormFieldsByAgencyGlobalType
                    isEmsAgency
                    eventForm={eventForm}
                    subdivisionsFieldsValues={emsSubdivisionsFieldsValues}
                    dispatchAreaValue={emsDispatchArea}
                    agencyValue={emsAgency}
                    primaryRadioChannelValue={emsPrimaryRadioChannel}
                />
            )}
            <Divider style={{ marginTop: '16px', marginBottom: '16px' }} />
            <DataList direction="row" style={{ marginBottom: '16px' }}>
                <DataItem dataKey="Call For Service" style={{ flexBasis: '50%' }}>
                    {callForService?.label}
                </DataItem>
                {additionalCallForService && (
                    <DataItem dataKey="Additional CFS" style={{ flexBasis: '50%' }}>
                        {displayMultiSelectFieldsValue(additionalCallForServices)}
                    </DataItem>
                )}
            </DataList>
            <DataList direction="row" style={{ marginBottom: '16px', gap: '40px' }}>
                {eventsHaveRiskLevel && (
                    <DataItem dataKey="Risk Level">{riskLevel?.label}</DataItem>
                )}
                <DataItem dataKey="Priority">{priority?.label}</DataItem>
                {isFireAgency && <DataItem dataKey="Alarm Level">{alarmLevel?.label}</DataItem>}
                <DataItem dataKey="Origin">{eventOrigin?.label}</DataItem>
                <DataItem dataKey="Disp">{eventQueue?.label}</DataItem>
            </DataList>
            {eventLabels && (
                <DataList style={{ marginBottom: '8px' }}>
                    <DataItem dataKey="Labels">
                        {eventLabels.length ? EventLabelsView({ labels: eventLabels }) : undefined}
                    </DataItem>
                </DataList>
            )}
        </Box>
    );
}
